import { useState, useRef } from "react";
import { useSelector } from "react-redux";
import { getFileUrl } from "../../../utils/helper";
import apiRequest from "../../../utils/admin/apiRequest";
import toast from "react-hot-toast";
import { FiSave, FiTrash2, FiEdit } from "react-icons/fi";
import InputField from "../../ui/form/InputField";

export default function HomepageBannerUpload() {
  const siteSetting = useSelector((state) => state.globalState.siteSetting);
  const initialBanners = siteSetting?.homepage_settings?.homepage_banners || [];

  const [banners, setBanners] = useState(
    initialBanners.map((banner) => ({
      imageId: banner._id,
      link: banner.link || "",
      image: null,
      preview: getFileUrl(banner.image),
      removeBg: false,
      existing: true,
    }))
  );

  const fileInputRefs = useRef([]);

  const handleAddBanner = () => {
    setBanners([
      ...banners,
      {
        link: "",
        image: null,
        preview: null,
        removeBg: false,
        existing: false,
      },
    ]);
  };

  const handleImageChange = (index, file) => {
    const newBanners = [...banners];
    newBanners[index].image = file;
    newBanners[index].preview = URL.createObjectURL(file);
    setBanners(newBanners);
  };

  const handleFieldChange = (index, field, value) => {
    const newBanners = [...banners];
    newBanners[index][field] = value;
    setBanners(newBanners);
  };

  const handleDelete = async (index) => {
    const banner = banners[index];
    if (banner.imageId) {
      try {
        await apiRequest.deleteHomePageBanner({
          imageId: banner.imageId,
        });
        toast.success("Banner deleted successfully");
      } catch (err) {
        toast.error("Failed to delete banner");
        return;
      }
    }

    const newBanners = [...banners];
    newBanners.splice(index, 1);
    setBanners(newBanners);
  };

  const handleSaveBanner = async (index) => {
    const banner = banners[index];
    if (!banner.link || (!banner.image && !banner.existing)) {
      toast.error("Please add both image and link");
      return;
    }

    const formData = new FormData();
    formData.append("link", banner.link);
    formData.append("removeBg", String(banner.removeBg));
    if (banner.image) {
      formData.append("image", banner.image);
    }

    try {
      if (banner.imageId) {
        await apiRequest.updateHomePageBanner({
          imageId: banner.imageId,
          formData,
        });
        toast.success("Banner updated");
      } else {
        const res = await apiRequest.uploadHomepageBanner({ formData });
        const newImageId = res.data?.imageId || res.data?.banner?._id;
        const updatedBanners = [...banners];
        updatedBanners[index].existing = true;
        updatedBanners[index].imageId = newImageId;
        setBanners(updatedBanners);
        toast.success("Banner uploaded");
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || "Upload failed");
    }
  };

  return (
    <div className="p-4 bg-white border shadow-sm rounded-2xl">
      <div className="flex items-center justify-between mb-5">
        <h2 className="text-lg font-semibold text-gray-800">
          Homepage Banners
        </h2>
      </div>

      <div className="grid gap-6 sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
        {banners.map((banner, index) => (
          <div key={index} className="p-4 transition border rounded-xl">
            <div
              className="relative w-full h-40 mb-3 cursor-pointer group"
              onClick={() => fileInputRefs.current[index]?.click()}
            >
              {banner.preview ? (
                <img
                  src={banner.preview}
                  alt="Banner Preview"
                  className="object-cover w-full h-full rounded-lg"
                />
              ) : (
                <div className="flex items-center justify-center w-full h-full text-sm text-gray-400 border-2 border-dashed rounded-lg">
                  Click to upload
                </div>
              )}

              <div className="absolute inset-0 flex items-center justify-center transition bg-black bg-opacity-0 rounded-lg group-hover:bg-opacity-30">
                <FiEdit
                  size={20}
                  className="text-white transition opacity-0 group-hover:opacity-100"
                />
              </div>

              <input
                ref={(el) => (fileInputRefs.current[index] = el)}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => handleImageChange(index, e.target.files?.[0])}
              />
            </div>

            <InputField
              label="Link"
              id={`link-${index}`}
              value={banner.link}
              onChange={(e) => handleFieldChange(index, "link", e.target.value)}
              size="sm"
            />

            <div className="flex items-center justify-between mt-4">
              <button
                onClick={() => handleSaveBanner(index)}
                className="flex items-center gap-1 px-3 py-1 text-sm text-green-600 rounded hover:text-green-700"
              >
                <FiSave /> Save
              </button>

              <button
                onClick={() => handleDelete(index)}
                className="flex items-center gap-1 px-3 py-1 text-sm text-red-500 rounded hover:text-red-600"
              >
                <FiTrash2 /> Delete
              </button>
            </div>
          </div>
        ))}
      </div>
      <div className="flex justify-end mt-3">
        <button
          onClick={handleAddBanner}
          className="px-4 py-2 text-[12px] font-medium text-white transition rounded-lg bg-primaryLight hover:bg-primaryDark"
        >
          + Add Banner
        </button>
      </div>
    </div>
  );
}
